import {
  AllGroup,
  type Button,
  DropdownButton,
  DropdownMenuItem,
  GroupDotName,
  NoneGroup,
  Toggle,
} from "@mixitone/components";
import { Group } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { isNil, isPresent, omit } from "@mixitone/util";
import { useCallback } from "react";

interface GroupSelectListProps {
  includeAll?: boolean;
  clubGroups: Group[];
  onSelectGroup: (group: Group | undefined) => void;
}

interface SingleSelectPropsBase {
  includeAll?: boolean;
  kind?: React.ComponentProps<typeof Button>["kind"];
  className?: string;
  name?: string;
  value: Group | undefined;
  compact?: boolean;
}

type SingleSelectPropsNoList = SingleSelectPropsBase & GroupSelectListProps;
type SingleSelectProps = SingleSelectPropsNoList;

type MultiSelectProps = Omit<
  SingleSelectPropsNoList,
  "value" | "onSelectGroup" | "includeAll" | "includeNone"
> & {
  value: Group[];
  onSelectGroup: (group: Group) => void;
  onUnselectGroup: (group: Group) => void;
};

const GroupSelectListComponent: React.FC<GroupSelectListProps> = ({
  includeAll,
  onSelectGroup,
  clubGroups,
}) => {
  return (
    <>
      {includeAll && (
        <DropdownMenuItem closeOnClick onClick={() => onSelectGroup(undefined)}>
          {includeAll && <AllGroup />}
        </DropdownMenuItem>
      )}
      {clubGroups.map((group) => (
        <DropdownMenuItem
          key={`group-dropdown-${group.id}`}
          closeOnClick
          onClick={() => onSelectGroup(group)}
        >
          <GroupDotName group={group} />
        </DropdownMenuItem>
      ))}
    </>
  );
};

const GroupSelectComponent: React.FC<React.PropsWithChildren<SingleSelectProps>> = (props) => {
  const { kind, className, name, value, includeAll, compact, children } = props;

  let buttonValue: React.ReactNode | null;

  if (isPresent(value) && value instanceof Group) {
    buttonValue = <GroupDotName group={value} />;
  } else {
    buttonValue = null;
  }

  if (isNil(value) && includeAll) {
    buttonValue = <AllGroup />;
  }

  return (
    <DropdownButton
      buttonProps={{
        kind: kind || "select",
        className,
        type: "button",
        name,
        compact,
        "data-testid": "group-select",
      }}
      button={buttonValue}
    >
      {() => (
        <>
          <GroupSelectListComponent {...omit(props, ["children"])} />
          {children}
        </>
      )}
    </DropdownButton>
  );
};

const GroupMultiSelectComponent: React.FC<MultiSelectProps> = ({
  clubGroups,
  onSelectGroup,
  onUnselectGroup,
  value,
  className,
  kind,
  name,
}) => {
  const handleItemClick = useCallback(
    (group: Group) => {
      if (value.includes(group)) {
        onUnselectGroup(group);
      } else {
        onSelectGroup(group);
      }
    },
    [value],
  );

  let buttonValue: React.ReactNode | null;

  if (Array.isArray(value) && value.length > 0) {
    buttonValue = value.map((v) => <GroupDotName key={v.id} group={v} className="gap-1" />);
  } else {
    buttonValue = null;
  }

  if (isNil(value) || value.length === 0) {
    buttonValue = <NoneGroup />;
  }

  return (
    <DropdownButton
      buttonProps={{ kind: kind || "select", className, type: "button", name }}
      button={buttonValue}
    >
      {clubGroups.map((group) => (
        <DropdownMenuItem
          key={`group-dropdown-${group.id}`}
          onClick={() => handleItemClick(group)}
          className="flex items-center gap-1"
        >
          <Toggle small checked={value.includes(group)} onChange={() => {}} />
          <GroupDotName group={group} />
        </DropdownMenuItem>
      ))}
    </DropdownButton>
  );
};

export const GroupSelect = ApplicationView(GroupSelectComponent);
export const GroupMultiSelect = ApplicationView(GroupMultiSelectComponent);
export const GroupSelectList = ApplicationView(GroupSelectListComponent);
